/**
 * The answers of a similarity search: the objects that look like a seed object, one snapshot for
 * each seed and scope, read while a gallery shows them. The search is ranked, best match first,
 * in one page with no cursor.
 */

import { type Atom, atom, type WritableAtom } from 'jotai';

import { queryAtoms } from '../queryAtoms';
import { api } from '@/api';
import { findFirstOfEachId } from '@/lib/firstOfEachId';
import type { EnrollmentChange } from '@/types/enrollments';
import type { EventSearchResult, EventSearchSnapshot } from '@/types/events';
import type { SimilarObjectsQuery } from '@/types/objects';
import type { QueryWrite } from '@/types/snapshot';

import { applyEnrollmentChange } from './eventSearchAtoms';

const searches = queryAtoms<
    SimilarObjectsQuery, EventSearchSnapshot, EventSearchResult[], EnrollmentChange
>({
    getRequestKey: ({ key, opts }): string => `${key}|${JSON.stringify(opts)}`,
    getPending: (): EventSearchSnapshot => ({
        events: [], isLoaded: false, hasOlder: false, olderPageCount: 0, isLoading: true,
        error: null,
    }),
    load: async ({ embedding, opts }): Promise<EventSearchResult[]> => (
        api.searchEventsByVector(embedding, opts)
    ),
    merge: (_previousSnapshot, rows): EventSearchSnapshot => ({
        events: findFirstOfEachId(rows),
        isLoaded: true,
        hasOlder: false,
        olderPageCount: 0,
        isLoading: false,
        error: null,
    }),
    apply: applyEnrollmentChange,
});

/** The objects that `query` finds; write `Reload` to search again. */
export function similarObjectsAtom(
    query: SimilarObjectsQuery,
): WritableAtom<EventSearchSnapshot, [QueryWrite<EventSearchSnapshot, EnrollmentChange>], void> {
    return searches.getMember(query);
}

/** What a reader with no search reads; there is nothing to load. */
export const noSimilarObjectsAtom: Atom<EventSearchSnapshot> = atom(
    (): EventSearchSnapshot => ({
        events: [], isLoaded: true, hasOlder: false, olderPageCount: 0, isLoading: false,
        error: null,
    }),
);

/** An enrollment editor's change, sent to each held search. */
export const applyEnrollmentChangeToSimilarObjectsAtom = searches.applyAll;
